<template>
  <ConfirmDialog
    :open="open"
    max-height="500"
    class-name="sso-config-dialog"
    @cancel="updateOpen(false)"
  >
    <template #title> Configure SSO </template>
    <template #detail>
      Please, enter a valid SAML SSO configuration.
      <br />
      Refer to our
      <a
        href="https://docs.v7labs.com/docs/single-sign-on-sso-configuration"
        target="_blank"
      >
        documentation
      </a>
      on what to insert below.
    </template>

    <template #body>
      <div class="sso-form__content">
        <TextArea
          id="ssoConfig"
          :model-value="ssoConfig"
          theme="transparent"
          required
          :disabled="saving || readonly"
          rows="12"
          type="text"
          name="ssoConfig"
          @update:model-value="emit('update:ssoConfig', $event)"
        />
        <Spinner
          v-if="saving"
          class="sso-form__spinner"
          size="md"
          color="dark"
          label="SSO configuration"
        />
      </div>
    </template>

    <template #footer-buttons>
      <DarwinButton
        size="md"
        flair="rounded"
        kind="outline"
        @click="updateOpen(false)"
      >
        {{ readonly ? 'Close' : 'Cancel' }}
      </DarwinButton>
      <DarwinButton
        v-if="!readonly"
        size="md"
        flair="rounded"
        kind="primary"
        :disabled="saving"
        @click="onSubmit"
      >
        Confirm
      </DarwinButton>
    </template>
  </ConfirmDialog>
</template>

<script lang="ts">
export default {
  name: 'SsoConfigModal',
}
</script>

<script lang="ts" setup>
import { ref } from 'vue'

import TextArea from '@/components/Common/TextArea.vue'
import { useToast } from '@/uiKit/Toast/useToast'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import { updateSSOConfig } from '@/backend/darwin/updateSSOConfig'
import { createSSOConfig } from '@/backend/darwin/createSSOConfig'

const props = defineProps<{
  open: boolean
  ssoConfig: string
  hasExistingConfig: boolean
  teamId: number
  readonly: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'update:ssoConfig', value: string): void
  (e: 'update:hasExistingConfig', value: boolean): void
}>()

const updateOpen = (value: boolean): void => emit('update:open', value)

const saving = ref<boolean>(false)
const toast = useToast()

const onSubmit = async (): Promise<void> => {
  saving.value = true

  const payload = { teamId: props.teamId, config: props.ssoConfig }
  const result = props.hasExistingConfig
    ? await updateSSOConfig(payload)
    : await createSSOConfig(payload)

  saving.value = false

  if (result.ok) {
    emit('update:ssoConfig', result.data.idp_metadata)
    emit('update:hasExistingConfig', true)
    toast.success({ meta: { title: 'SSO config successfully saved' } })
    updateOpen(false)
  } else {
    toast.warning({ meta: { title: result.error.message as string } })
  }
}
</script>

<style lang="scss" scoped>
.sso-form__content {
  position: relative;
  padding: 15px;
}
.sso-form__spinner {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
}
</style>
